import { useState, useImperativeHandle, forwardRef, useEffect } from "react";
import { startOfWeek, addDays, subWeeks, startOfMonth, endOfMonth, subMonths, addMonths, endOfDay } from "date-fns";
import { Menu, MenuItem, Popover, Box, Button } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import "../../styles/FilterPill.css";
import { normalizeToFullDay } from "../../utils/utils";
import ColumnFilterIcon from "../../assets/action-items/column-filter-icon.svg";
import { useNavigate } from "react-router-dom";

export type FilterDatePickerHandle = {
  openDropdown: (anchor?: HTMLElement) => void;
};

type FilterDatePickerProps = {
  onDateChange: (selection: {
    preset: string;
    startDate: string;
    endDate: string;
  }) => void;
  value?: {
    preset: string;
    startDate?: string;
    endDate?: string;
  };
  limitFutureDays?: number;
  presetOptions?: Array<{
    label: string;
    value: string;
  }>;
  showMenuHeader?: boolean;
  maxRangeInMonths?: number;
};

const FilterDatePicker = forwardRef<
  FilterDatePickerHandle,
  FilterDatePickerProps
>(({ onDateChange, value, limitFutureDays, presetOptions, showMenuHeader = false, maxRangeInMonths }, ref) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [isCustomOpen, setIsCustomOpen] = useState(false);
  const nextYear = new Date().getFullYear() + 1;
  const today = new Date();
  const maxSelectableDate =
  limitFutureDays !== undefined
    ? addDays(today, limitFutureDays)
    : new Date(nextYear, 11, 31);  
  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;

  const [tempRange, setTempRange] = useState<{
    startDate?: Date | null;
    endDate?: Date | null;
  }>({startDate: null,
    endDate: null,});
  const [selectedPreset, setSelectedPreset] = useState<string>("");
  const [initialized, setInitialized] = useState(false); // prevents infinite loops
  const [view, setView] = useState("day");
  const navigate = useNavigate();

  const openDropdown = (anchor?: HTMLElement) => {
    if (anchor) setAnchorEl(anchor);
  };
  useImperativeHandle(ref, () => ({ openDropdown }));

  const closeMenu = () => setAnchorEl(null);


  const isRangeChanged =
  tempRange.startDate?.getTime() !== new Date(value?.startDate || "").getTime() ||
  tempRange.endDate?.getTime() !== new Date(value?.endDate || "").getTime();

  const maxEndDate = tempRange.startDate && maxRangeInMonths
    ? addMonths(tempRange.startDate, maxRangeInMonths)
    : maxSelectableDate;

  const finalMaxEndDate = maxEndDate > maxSelectableDate ? maxSelectableDate : maxEndDate;

  const isInvalidRange =
    tempRange.startDate &&
    tempRange.endDate &&
    maxRangeInMonths &&
    tempRange.endDate > endOfDay(addMonths(tempRange.startDate, maxRangeInMonths));

  //  Helper 2: get date range for preset (matches CustomDatePicker)
  const getPresetRange = (preset: string) => {
    const today = new Date();
    let startDate = today;
    let endDate = today;

    switch (preset) {
      case "today":
        return normalizeToFullDay(today, today);

      case "tomorrow": {
        const tomorrow = addDays(today, 1);
        return normalizeToFullDay(tomorrow, tomorrow);
      }

      case "currentWeek": {
        const start = startOfWeek(today, { weekStartsOn: 0 });
        let end = addDays(start, 6);
        if (limitFutureDays !== undefined) {
          const maxDate = addDays(today, limitFutureDays);
          if (end > maxDate) end = maxDate;
        }
        return normalizeToFullDay(start, end);
      }

      case "currentMonth": {
        const start = startOfMonth(today);
        let end = endOfMonth(today);
        if (limitFutureDays !== undefined) {
          const maxDate = addDays(today, limitFutureDays);
          if(end > maxDate) end = maxDate
        }
        return normalizeToFullDay(start, end);
      }

      case "lastWeek": {
        const start = startOfWeek(subWeeks(today, 1), { weekStartsOn: 0 });
        const end = addDays(start, 6);
        return normalizeToFullDay(start, end);
      }

      case "lastMonth": {
        const start = startOfMonth(subMonths(today, 1));
        const end = endOfMonth(subMonths(today, 1));
        return normalizeToFullDay(start, end);
      }
      case "lastThreeMonths": {
        const start = subMonths(today, 3);
        const end = today;
        return normalizeToFullDay(start, end);
      }

      default:
        return normalizeToFullDay(startDate, endDate);
    }
  };

  const dateOptions = presetOptions ?? [
    { label: "Today", value: "today" },
    { label: "Current Week", value: "currentWeek" },
    { label: "Current Month", value: "currentMonth" },
    { label: "Last Week", value: "lastWeek" },
    { label: "Last Month", value: "lastMonth" },
    { label: "Custom", value: "custom" },
  ];

  // When a user selects a preset from menu
  const handleDateSelection = (preset: string) => {
    if (preset === "custom") {
      setSelectedPreset("custom");
    
      // Prefill with current preset's start/end (if available)
      if (value?.startDate && value?.endDate) {
        const start = new Date(value.startDate);
        const end = new Date(value.endDate);
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          setTempRange(normalizeToFullDay(start, end));
        } else if (selectedPreset && selectedPreset !== "custom") {
          const { startDate, endDate } = getPresetRange(selectedPreset);
          setTempRange({ startDate, endDate });
        }
      } else if (selectedPreset && selectedPreset !== "custom") {
        const { startDate, endDate } = getPresetRange(selectedPreset);
        setTempRange({ startDate, endDate });
      } else if (!tempRange.startDate || !tempRange.endDate) {
        // fallback: default to today if empty
        const today = new Date();
        setTempRange(normalizeToFullDay(today, today));
      }
    
      // Close dropdown first for smooth transition
      closeMenu();
    
      // Then open popover after menu animation finishes
      setTimeout(() => {
        if (anchorEl) {
          const rect = anchorEl.getBoundingClientRect();
          setPopoverAnchor({ top: rect.bottom, left: rect.left });
          setIsCustomOpen(true);
        }
      }, 100);
    
      return;
    }
    const { startDate, endDate } = getPresetRange(preset);

    setSelectedPreset(preset);
    onDateChange({
      preset,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });

    closeMenu();
  };

  // When an external value changes (controlled mode)
  useEffect(() => {
    if (!value?.preset) return;
    if (initialized && value.preset === selectedPreset) return;

    let startDate: Date | undefined;
    let endDate: Date | undefined;

    if (value.preset === "custom" && value.startDate && value.endDate) {
      const startParsed = new Date(value.startDate);
      const endParsed = new Date(value.endDate);
      if (!isNaN(startParsed.getTime()) && !isNaN(endParsed.getTime())) {
        const normalized = normalizeToFullDay(startParsed, endParsed);
        setTempRange(normalized);
      }
    } else {
      const range = getPresetRange(value.preset);
      startDate = range.startDate;
      endDate = range.endDate;
    }

    if (startDate && endDate) {
      onDateChange({
        preset: value.preset,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });
    }

    setSelectedPreset(value.preset);
    setInitialized(true);
  }, [value]);

  const handleClick = () => {
    navigate("/settings/user-settings?tab=CONTROLS");
  };

  return (
    <>
      {/* 🔹 Dropdown menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: "fdp-menu-paper" }}
      >
        {showMenuHeader && (
          <Box className="fdp-menu-header">
            <span className="fdp-menu-header-title">Select</span>
            <img 
              src={ColumnFilterIcon}
              style={{
                cursor: "pointer",
                transition: "0.2s",
              }}
              onMouseOver={(e) => (e.currentTarget.style.filter = "brightness(0)")}
              onMouseOut={(e) => (e.currentTarget.style.filter = "none")}
              onClick={handleClick}
            />
          </Box>
        )}
        {dateOptions.map((option) => (
          <MenuItem 
            key={option.value}
            className={`fdp-menu-item ${selectedPreset === option.value ? "active" : ""}`}
            onClick={() => handleDateSelection(option.value)}
          >
            <span className="fdp-option-text">{option.label}</span>
            <span
              className={`fdp-option-circle ${
                selectedPreset === option.value ? "filled" : "empty"
              }`}
            ></span>
          </MenuItem>
        ))}
      </Menu>

      {/* Custom date range popover */}
      <Popover
        open={isCustomOpen}
        anchorReference="anchorPosition"
        anchorPosition={popoverAnchor || undefined}
        onClose={() => setIsCustomOpen(false)}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: "fdp-popover" }}
      >
        <Box className="fdp-popover-content">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={tempRange.startDate || null}
              onChange={(newValue) => {
                setTempRange((prev) => {
                  // If user clears start date
                  if (!newValue) {
                    return { ...prev, startDate: undefined, endDate: undefined };
                  }
          
                  // If new start date is after existing end date → auto-adjust
                  if (prev.endDate && newValue > prev.endDate) {
                    return { ...prev, startDate: newValue, endDate: newValue };
                  }
          
                  return { ...prev, startDate: newValue };
                });
              }}
              format="MMM dd, yyyy"
              minDate={new Date(lastYear, 0, 1)}
              maxDate={maxSelectableDate}
              views={["year", "month", "day"]}
              onViewChange={(newView) => setView(newView)} // Track view state
              onOpen={() => setView("day")}
              slotProps={{
                textField: { fullWidth: true, variant: "outlined" },
                desktopPaper: {
                  className: `custom-date-picker-paper ${view === "year" ? "year-view" : ""}`,
                },
              }}
            />
            <DatePicker
              label="End Date"
              value={tempRange.endDate || null}
              onChange={(newValue) => {
                setTempRange((prev) => {
                  // If cleared → reset end date
                  if (!newValue) return { ...prev, endDate: undefined };
          
                  // Only allow selecting dates >= startDate
                  if (prev.startDate && newValue < prev.startDate) {
                    return prev; // ignore invalid selection
                  }
          
                  return { ...prev, endDate: newValue };
                });
              }}
              format="MMM dd, yyyy"
              minDate={tempRange.startDate} // Prevents selecting an end date before the start date
              maxDate={finalMaxEndDate}
              views={["year", "month", "day"]}
              onViewChange={(newView) => setView(newView)} // Track view state
              onOpen={() => setView("day")}
              slotProps={{
                textField: { fullWidth: true, variant: "outlined" },
                desktopPaper: {
                  className: `custom-date-picker-paper ${view === "year" ? "year-view" : ""}`,
                },
              }}
            />
          </LocalizationProvider>
          <Button
            className="fdp-apply-button"
            disabled={
              !tempRange.startDate ||
              !tempRange.endDate ||
              isInvalidRange ||
              !isRangeChanged
            }
            onClick={() => {
              if (tempRange.startDate && tempRange.endDate) {
                const { startDate, endDate } = normalizeToFullDay(
                  tempRange.startDate,
                  tempRange.endDate
                );    
                onDateChange({
                  preset: "custom",
                  startDate: startDate.toISOString(),
                  endDate: endDate.toISOString(),
                });
              }
              setIsCustomOpen(false);
            }}
          >
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  );
});

export default FilterDatePicker;
